import type { I18nMessage } from "@pollen/core/model/i18n.model";

import type { RouteLocationRaw } from "#vue-router";

type PriceView = DatabaseCompositeType<"money_amount"> & {
  formatted: string;
  htmlFormatted: string;
};

export type CoursePackageWithDetailsView = {
  price: PriceView;
  format?: DatabaseCompositeType<"course_format">;
  contentItems: I18nMessage[];
  featured?: { label: I18nMessage; icons: string[] };
  keys: string[];
  type: "BASIC" | "PREMIUM" | "PRIVATE";
  _links?: { enrol?: RouteLocationRaw };
};

export function useCoursePackages({
  course,
  course_session_id,
}: {
  course: Pick<DatabaseTable<"courses">, "id" | "format" | "slug"> & {
    teacher: Pick<DatabaseTable<"teachers">, "first_name">;
  };
  course_session_id?: DatabaseTable<"course_sessions">["id"];
}) {
  const i18n = useI18n();

  const getPriceViewFromMoneyAmount = (
    moneyAmount: DatabaseCompositeType<"money_amount">,
  ): PriceView => {
    return {
      ...moneyAmount,
      formatted: useI18nMoney(moneyAmount, { excludingTaxesSuffix: true, i18n })
        .value,
      htmlFormatted: useI18nMoney(moneyAmount, {
        excludingTaxesSuffix: true,
        html: true,
        i18n,
      }).value,
    };
  };

  const { data } = useLazyAsyncData(async () => {
    //eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore excessive stack depth
    return $fetch(`/api/courses/${course.id}/products`, {
      query: {
        session: course_session_id,
      },
    });
  });

  const packages = computed<CoursePackageWithDetailsView[]>(() => {
    if (!data.value) return [];

    const baseProductViewItems: I18nMessage[] = [
      i18n.t("course.package.content.course", {
        format: useCourseFormatLabel(course.format, { i18n }).value,
        name: course.teacher.first_name,
      }),
      ...(data.value.session?.options.lunch_included
        ? [i18n.t("course.package.content.lunch")]
        : []),
      i18n.t("course.package.content.documents"),
    ];

    const baseProduct = data.value.products.find((p) => p.key !== "MENTORING");
    const mentoringProduct = data.value.products.find(
      (p) => p.key === "MENTORING",
    );
    const views: CoursePackageWithDetailsView[] = [];

    if (baseProduct) {
      views.push({
        price: getPriceViewFromMoneyAmount(baseProduct.price),
        format: course.format,
        contentItems: baseProductViewItems,
        featured: mentoringProduct
          ? {
              label: i18n.t("course.package.basic_content_label"),
              icons: ["ph:graduation-cap"],
            }
          : undefined,
        type: "BASIC",
        keys: [baseProduct.key],
        _links: {
          enrol: data.value.session
            ? {
                name: "courses-slug-sessions-sessionId-enrol-index",
                params: {
                  slug: data.value.session.course!.slug,
                  sessionId: data.value.session.id,
                },
                query: {
                  packageKeys: baseProduct.key,
                },
              }
            : undefined,
        },
      });
    }

    if (baseProduct && mentoringProduct) {
      views.push({
        price: getPriceViewFromMoneyAmount({
          amount:
            (baseProduct.price.amount ?? 0) +
            (mentoringProduct.price.amount ?? 0),
          currency: baseProduct.price.currency ?? "",
        }),
        format: course.format,
        contentItems: [
          ...baseProductViewItems,
          i18n.t("course.package.content.mentoring", {
            name: course.teacher.first_name,
          }),
        ],
        featured: {
          label: i18n.t("course.package.premium_content_label"),
          icons: ["ph:graduation-cap", "ph:handshake"],
        },
        type: "PREMIUM",
        keys: [baseProduct.key, mentoringProduct.key],

        _links: {
          enrol: data.value.session
            ? {
                name: "courses-slug-sessions-sessionId-enrol-index",
                params: {
                  slug: data.value.session.course!.slug,
                  sessionId: data.value.session.id,
                },
                query: {
                  packageKeys: [baseProduct.key, mentoringProduct.key],
                },
              }
            : undefined,
        },
      });
    }

    return views;
  });

  return { packages };
}
